import React, { useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Typography,
  FormControl,
  TextField,
  Select,
  MenuItem
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-input-2";
import { ReactComponent as DownArrow } from "../../../../../assets/AccountingModule/arrow_down.svg";
import color from "../../../../../constants/color";
import { CreateSupplierValidation } from "../../../../../helper/Validation";
import style from "./style";


const AddNewSuppliersPage = ({ loading, handleAddNewSupplier }) => {
  const options = countryList().getData();
  const mobileNumberRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    creditLimits: "",
  });

  const [address, setAddress] = useState({
    billing: {
      country: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      pinCode: "",
      phone: "",
    },
    shipping: {
      country: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      pinCode: "",
      phone: "",
    },
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    creditLimits: "",
    billing: {
      country: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      pinCode: "",
      phone: "",
    },
    shipping: {
      country: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      pinCode: "",
      phone: "",
    },
  });

  // Validate a field based on rules
  const validateField = (fieldName, value, isAddress = false, addressType = null) => {
    let rule;

    if (isAddress && addressType) {
      rule = CreateSupplierValidation[fieldName];
    } else {
      rule = CreateSupplierValidation[fieldName];
    }

    if (!rule) return true; // No rule defined, consider valid

    // Check if required field is empty
    if (rule.required && !value) {
      return rule.validator.errEmpty;
    }

    // Skip validation for empty non-required fields
    if (!rule.required && !value) {
      return "";
    }

    // Validate against regex pattern
    if (!rule.validator.regEx.test(value)) {
      return rule.validator.error;
    }

    return ""; // No error
  };

  const handleFormChange = (e) => {
    if (!e || !e.target) return;

    const { name, value } = e.target;
    if (!name) return;

    // Validate the field
    const error = validateField(name, value);

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (type, field, value) => {
    // Validate the field
    const error = validateField(field, value, true, type);

    setErrors(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: error
      }
    }));

    setAddress(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      creditLimits: formData.creditLimits,
      billingAddress: address.billing,
      shippingAddress: address.shipping,
    };
    handleAddNewSupplier(finalData);
  };

  const handleCopyBillingToShipping = () => {
    setAddress(prev => ({
      ...prev,
      shipping: { ...prev.billing }
    }));

    setErrors(prev => ({
      ...prev,
      shipping: { ...prev.billing }
    }));
  };

  // Common styles for text fields
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-error fieldset': {
        borderColor: 'red',
      },
    },
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Add New Supplier</Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} lg={11.7} sx={style.gridbox}>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Name</Typography>
                <TextField
                  variant="outlined"
                  required
                  placeholder="Enter Name"
                  fullWidth
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  autoComplete="name"
                  sx={{ ...style.addressTextField, ...textFieldStyles, mt: 1 }}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Email</Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  placeholder="Enter Email"
                  sx={{ ...style.addressTextField, ...textFieldStyles, mt: 1 }}
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
              <Box sx={style.inputBox}>
                <FormControl fullWidth sx={style.countryCode}>
                  <Typography sx={{ ...style.inputText, mb: 1 }}>Phone</Typography>
                  <PhoneInput
                    country={"za"}
                    enableSearch={true}
                    value={formData.phone}
                    onChange={(value, country) => {
                      const fullNumber = `+${value}`;
                      setFormData((prev) => ({
                        ...prev,
                        phone: fullNumber,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        phone: fullNumber ? "" : "Phone is required",
                      }));
                    }}
                    sx={style.mobile}
                    inputProps={{
                      style: {
                        height: "1em",
                        borderRadius: "8px",
                      },
                    }}

                  />
                </FormControl>
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Credit Limits</Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter Credit Limits"
                  fullWidth
                  id="creditLimits"
                  sx={{ ...style.addressTextField, ...textFieldStyles, mt: 1 }}
                  name="creditLimits"
                  value={formData.creditLimits}
                  onChange={handleFormChange}
                  autoComplete="creditLimits"
                  error={!!errors.creditLimits}
                  helperText={errors.creditLimits}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Box sx={style.addressBox}>
                <Box sx={{ width: "50%" }}>
                  <Typography sx={{ fontWeight: "Bold" }}>
                    Billing Address
                  </Typography>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Country/Region</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <FormControl fullWidth required sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "2.8em",
                          marginTop: "0.5em",
                          borderRadius: "10px"
                        }
                      }}>
                        <Select
                          value={address.billing.country || ""}
                          onChange={(e) => handleAddressChange('billing', 'country', e.target.value)}
                          name="billingCountry"
                          sx={style.dropDownMenu}
                          displayEmpty
                        >
                          <MenuItem disabled value="">Select</MenuItem>
                          {options.map((country, index) => (
                            <MenuItem key={index} sx={style.dropDownMenu} value={country.label}>{country.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Address</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Street 1"
                        value={address.billing.street1}
                        onChange={(e) => handleAddressChange('billing', 'street1', e.target.value)}
                        autoComplete="bstreet1"
                        sx={{ ...style.addressTextField, ...textFieldStyles, mb: 3 }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.billing.street1}
                        helperText={errors.billing.street1}
                      />
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Street 2"
                        value={address.billing.street2}
                        onChange={(e) => handleAddressChange('billing', 'street2', e.target.value)}
                        autoComplete="bstreet2"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>City</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="City"
                        value={address.billing.city}
                        onChange={(e) => handleAddressChange('billing', 'city', e.target.value)}
                        autoComplete="bcity"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.billing.city}
                        helperText={errors.billing.city}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>State</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="State"
                        value={address.billing.state}
                        onChange={(e) => handleAddressChange('billing', 'state', e.target.value)}
                        autoComplete="bstate"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.billing.state}
                        helperText={errors.billing.state}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Pin Code</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Pin Code"
                        value={address.billing.pinCode}
                        onChange={(e) => handleAddressChange('billing', 'pinCode', e.target.value)}
                        autoComplete="bpinCode"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.billing.pinCode}
                        helperText={errors.billing.pinCode}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Phone</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <PhoneInput
                        country={"za"}
                        enableSearch={true}
                        value={address.billing.phone}
                        onChange={(value) => {
                          const fullNumber = `+${value}`;
                          handleAddressChange("billing", "phone", fullNumber);
                        }}
                        inputProps={{
                          name: "bphone",
                          autoComplete: "bphone",
                        }}
                        inputStyle={{
                          width: "100%",
                          fontSize: "14px",
                          fontWeight: 400,
                          borderRadius: "8px",
                          height: "40px",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ width: "50%" }}>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Typography sx={{ fontWeight: "Bold" }}>
                      Shipping Address
                    </Typography>
                    <Typography
                      sx={{ color: color.secondary, cursor: "pointer" }}
                      onClick={handleCopyBillingToShipping}
                    >
                      {"( "}
                      <DownArrow /> Copy Billing Address{" )"}
                    </Typography>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Country/Region</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <FormControl fullWidth required sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "2.8em",
                          marginTop: "0.5em",
                          borderRadius: "10px"
                        }
                      }}>
                        <Select
                          value={address.shipping.country || ""}
                          onChange={(e) => handleAddressChange('shipping', 'country', e.target.value)}
                          name="country"
                          sx={style.dropDownMenu}
                          displayEmpty
                        >
                          <MenuItem disabled value="">Select</MenuItem>
                          {options.map((country, index) => (
                            <MenuItem key={index} sx={style.dropDownMenu} value={country.label}>{country.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Address</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Street 1"
                        value={address.shipping.street1}
                        onChange={(e) => handleAddressChange('shipping', 'street1', e.target.value)}
                        autoComplete="sstreet1"
                        sx={{ ...style.addressTextField, ...textFieldStyles, mb: 3 }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.shipping.street1}
                        helperText={errors.shipping.street1}
                      />
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Street 2"
                        value={address.shipping.street2}
                        onChange={(e) => handleAddressChange('shipping', 'street2', e.target.value)}
                        autoComplete="sstreet2"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>City</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="City"
                        value={address.shipping.city}
                        onChange={(e) => handleAddressChange('shipping', 'city', e.target.value)}
                        autoComplete="scity"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.shipping.city}
                        helperText={errors.shipping.city}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>State</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="State"
                        value={address.shipping.state}
                        onChange={(e) => handleAddressChange('shipping', 'state', e.target.value)}
                        autoComplete="sstate"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.shipping.state}
                        helperText={errors.shipping.state}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Pin Code</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        placeholder="Pin Code"
                        value={address.shipping.pinCode}
                        onChange={(e) => handleAddressChange('shipping', 'pinCode', e.target.value)}
                        autoComplete="spinCode"
                        sx={{ ...style.addressTextField, ...textFieldStyles }}
                        InputProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 400,
                          },
                        }}
                        error={!!errors.shipping.pinCode}
                        helperText={errors.shipping.pinCode}
                      />
                    </Box>
                  </Box>
                  <Box sx={style.addressInput}>
                    <Box sx={{ width: "20%" }}>
                      <Typography sx={style.inputText}>Phone</Typography>
                    </Box>
                    <Box sx={{ width: "75%" }}>
                      <PhoneInput
                        country={"za"}
                        enableSearch={true}
                        value={address.shipping.phone}
                        onChange={(value) => {
                          const fullNumber = `+${value}`;
                          handleAddressChange("shipping", "phone", fullNumber);
                        }}
                        inputProps={{
                          name: "sphone",
                          autoComplete: "sphone",
                        }}
                        inputStyle={{
                          width: "100%",
                          fontSize: "14px",
                          fontWeight: 400,
                          borderRadius: "8px",
                          height: "40px",
                        }}
                      />
                    </Box>

                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Box sx={style.mainBoxes}>
            <Box sx={style.createJob}>
              <LoadingButton
                loading={loading}
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !formData.phone ||
                  !address.billing.country ||
                  !address.billing.street1 ||
                  !address.billing.city ||
                  !address.billing.state ||
                  !address.billing.pinCode ||
                  !address.shipping.country ||
                  !address.shipping.street1 ||
                  !address.shipping.city ||
                  !address.shipping.state ||
                  !address.shipping.pinCode ||
                  !!errors.name ||
                  !!errors.email ||
                  !!errors.phone ||
                  !!errors.billing.country ||
                  !!errors.billing.street1 ||
                  !!errors.billing.street2 ||
                  !!errors.billing.city ||
                  !!errors.billing.state ||
                  !!errors.billing.pinCode ||
                  !!errors.shipping.country ||
                  !!errors.shipping.street1 ||
                  !!errors.shipping.street2 ||
                  !!errors.shipping.city ||
                  !!errors.shipping.state ||
                  !!errors.shipping.pinCode
                }
                variant="contained"
                type="submit"
                color="success"
                sx={style.jobButtons}
              >
                Save as New
              </LoadingButton>
            </Box>
          </Box>
        </Grid>
      </form>
    </>
  );
};

export default AddNewSuppliersPage;